import { useCallback, useEffect, useRef, useState } from 'react';

import { getCampaignLogs } from '../../api/scriptsApi';
import { isActiveStatus, mergeLogLines } from './scriptStatus';

const ACTIVE_POLL_MS = 2_000;
const IDLE_POLL_MS = 8_000;
const ERROR_POLL_MS = 5_000;

/**
 * Polls one campaign's log trail incrementally.
 *
 * The server's cursor is an index into the append-only log, so each poll asks only
 * for what is new and appends it. One request at a time: the next poll is scheduled
 * when the previous one settles, so a slow server never gets a pile of overlapping
 * polls. Quick while the campaign is running or stopping, slow once it has ended.
 *
 * Returns { campaign, lines, truncatedBefore, error, notFound, isLoading }.
 */
export function useCampaignLogs(campaignRef) {
  const [state, setState] = useState({
    campaign: null,
    lines: [],
    truncatedBefore: false,
    error: null,
    notFound: false,
    isLoading: true
  });

  const cursorRef = useRef(null);

  const applyResponse = useCallback((data) => {
    if (data.reset) cursorRef.current = null;
    cursorRef.current = data.nextCursor;

    setState((prev) => {
      const base = data.reset ? [] : prev.lines;
      const lines = mergeLogLines(base, [...(data.events ?? []), ...(data.entries ?? [])]);
      return {
        campaign: data.campaign,
        lines,
        truncatedBefore: prev.lines.length === 0 || data.reset ? Boolean(data.truncatedBefore) : prev.truncatedBefore,
        error: null,
        notFound: false,
        isLoading: false
      };
    });

    return data.campaign?.status;
  }, []);

  useEffect(() => {
    if (!campaignRef) return undefined;

    let cancelled = false;
    let timer = null;
    let controller = null;
    cursorRef.current = null;

    async function poll() {
      controller = new AbortController();
      let delay = ERROR_POLL_MS;

      try {
        const data = await getCampaignLogs(campaignRef, cursorRef.current ?? undefined, controller.signal);
        if (cancelled) return;
        const status = applyResponse(data);
        delay = isActiveStatus(status) ? ACTIVE_POLL_MS : IDLE_POLL_MS;
      } catch (err) {
        if (cancelled || err?.name === 'AbortError') return;
        if (err?.status === 404) {
          setState((prev) => ({ ...prev, notFound: true, isLoading: false, error: null }));
          return; // Nothing will appear at this reference; stop polling.
        }
        setState((prev) => ({ ...prev, error: err, isLoading: false }));
      }

      if (!cancelled) timer = setTimeout(poll, delay);
    }

    poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      controller?.abort();
    };
  }, [campaignRef, applyResponse]);

  return state;
}
